import { useEffect } from 'react';

import { format } from 'date-fns';
import { sortBy } from 'helpers';
import { observer, useLocalObservable } from 'mobx-react-lite';

import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpen } from '@components/icons/open';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { Box } from 'components/Box';
import { DataTable } from 'components/DataTable';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { usePermissions } from 'features/permissions';
import { configs } from 'jobboards/configs';
import { useStore } from 'models/Provider';

import { PromoteCreateButton } from './PromoteCreateButton';
import { PromoteEmptyPostList } from './PromoteEmptyPostList';

export const PromotePostList = observer(({ jobID }) => {
  const { api, t, userStore } = useStore();
  const { hasRolePermission } = usePermissions();

  const local = useLocalObservable(() => ({
    posts: [],
    status: 'loading',
    setPosts(posts) {
      local.posts = posts;
      local.status = 'loaded';
    },
    loadPosts() {
      api.jobboards.loadPostsForJob(jobID).then((res) => {
        if (!res.error) {
          local.setPosts(sortBy(res.data, 'id', 'desc'));
        }
      });
    },
  }));

  useEffect(() => {
    local.loadPosts();
  }, [local]);

  if (local.status === 'loading') {
    return <Loading />;
  }

  if (local.posts.length === 0) {
    return <PromoteEmptyPostList />;
  }

  const columns = [
    {
      title: t('jobboards.table.jobboard'),
      width: 2,
      render: (item) => <div>{configs[item.jobBoard].title}</div>,
    },
    {
      title: t('jobboards.table.posted'),
      width: 0.75,
      render: ({ created }) => <div>{format(new Date(created), 'dd.MM.yyyy')}</div>,
    },
    {
      title: t('jobboards.table.user'),
      width: 1.5,
      render: ({ user }) => <div>{userStore.items.get(user).name}</div>,
    },
    {
      title: t('jobboards.table.link'),
      width: 0.75,
      render: ({ postUrl }) => {
        if (postUrl) {
          return (
            <Box flex alignItems="center">
              <IconOpen style={{ marginRight: '0.75rem' }} />
              <a href={postUrl} target="_blank" rel="noopener noreferrer">
                {t('jobboards.list.view')}
              </a>
            </Box>
          );
        }
        return '-';
      },
    },
  ];

  if (hasRolePermission('jobboards:manage')) {
    columns.push({
      title: t('jobboards.table.options'),
      width: 0.5,
      style: { display: 'flex', justifyContent: 'flex-end', textAlign: 'right' },
      render: (item) => <RenderOptions item={item} load={local.loadPosts} />,
    });
  }

  return (
    <>
      <PromoteCreateButton />
      <PageContent alwaysScroll={false}>
        <Text font="heading2" text={t('jobboards.postlist.header')} />
        <Text font="regular14" color="purple60" text={t('jobboards.postlist.text')} />
        <Box mt="16">
          <DataTable
            spacing={false}
            data={local.posts}
            columns={columns}
            rowStyle={{ backgroundColor: 'transparent' }}
          />
        </Box>
      </PageContent>
    </>
  );
});

const RenderOptions = observer(({ item, load }) => {
  const { api, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const state = useLocalObservable(() => ({
    loading: false,
    setLoading(val) {
      state.loading = val;
    },
  }));

  const remove = async () => {
    if (state.loading) return;
    state.setLoading(true);
    const res = await api.jobboards.removeJobPost(item.id);
    state.setLoading(false);
    if (!res.error) {
      showSuccess(t('jobboards.removepost.success'));
      await load();
    } else {
      showError(t('jobboards.removepost.error'));
    }
  };

  return (
    <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
      <Menu.Item
        onSelect={state.loading ? undefined : remove}
        variant="danger"
        label={t('jobboards.list.menu.remove')}
        icon={<IconTrash />}
      />
    </Menu>
  );
});
